import { STORAGE_KEY } from "../../i18n/LanguageContext";
import { LANGUAGES, DEFAULT_LANGUAGE_CODE } from "../../i18n/languages";

// "prs" (Dari) needs an explicit Gregorian calendar override — its default
// Intl calendar is the Afghan solar Hijri calendar (e.g. year 1405 instead
// of 2026), which would disagree with the Gregorian dates everywhere else
// in the app and in the backend.
const INTL_LOCALE_OVERRIDES = {
   prs: "prs-u-ca-gregory"
};

// Mirrors the non-hook `translate()` helper in LanguageContext.js: reads the
// same localStorage key directly so plain (non-component) call sites — table
// column renderers, PDF/report exports, etc. — stay in sync with the
// language picker without needing React context.
export function getCurrentLocaleTag() {
   let code = DEFAULT_LANGUAGE_CODE;
   try {
      code = localStorage.getItem(STORAGE_KEY) || DEFAULT_LANGUAGE_CODE;
   } catch (e) {
      /* localStorage unavailable — fall back to default */
   }
   if (!LANGUAGES.some((language) => language.code === code)) {
      code = DEFAULT_LANGUAGE_CODE;
   }
   return INTL_LOCALE_OVERRIDES[code] || code;
}

// Intl.DateTimeFormat/RelativeTimeFormat instances are meant to be reused —
// constructing one per table cell on every render is the "repeated expensive
// formatting operations" the task warns against. Cache is keyed by locale +
// options, capped implicitly by the small number of languages/options in use.
const dateFormatterCache = new Map();

function getDateFormatter(options) {
   const locale = getCurrentLocaleTag();
   const cacheKey = locale + "|" + JSON.stringify(options || {});
   let formatter = dateFormatterCache.get(cacheKey);
   if (!formatter) {
      formatter = new Intl.DateTimeFormat(locale, { calendar: "gregory", ...options });
      dateFormatterCache.set(cacheKey, formatter);
   }
   return formatter;
}

const relativeFormatterCache = new Map();

function getRelativeFormatter() {
   const locale = getCurrentLocaleTag();
   let formatter = relativeFormatterCache.get(locale);
   if (!formatter) {
      formatter = new Intl.RelativeTimeFormat(locale, { numeric: "auto" });
      relativeFormatterCache.set(locale, formatter);
   }
   return formatter;
}

function toValidDate(date) {
   if (date == null) return null;
   const d = date instanceof Date ? date : new Date(date);
   return isNaN(d.getTime()) ? null : d;
}

const DEFAULT_DATE_OPTIONS = { year: "numeric", month: "long", day: "numeric" };
const DEFAULT_DATETIME_OPTIONS = {
   year: "numeric",
   month: "long",
   day: "numeric",
   hour: "numeric",
   minute: "2-digit"
};

// Locale-aware date formatting for the app's currently selected language.
// Accepts a Date, an ISO string, or a timestamp; returns "" for null,
// undefined, or unparseable input instead of throwing or rendering
// "Invalid Date"/"NaN-NaN-NaN".
export function formatDate(date, options = DEFAULT_DATE_OPTIONS) {
   const d = toValidDate(date);
   return d ? getDateFormatter(options).format(d) : "";
}

export function formatDateTime(date, options = DEFAULT_DATETIME_OPTIONS) {
   const d = toValidDate(date);
   return d ? getDateFormatter(options).format(d) : "";
}

export function formatTime(date, options = { hour: "numeric", minute: "2-digit" }) {
   const d = toValidDate(date);
   return d ? getDateFormatter(options).format(d) : "";
}

// "3 hours ago" / "in 2 days" style relative formatting, e.g. for
// notification timestamps — native Intl.RelativeTimeFormat, no hand-rolled
// English word list to translate.
export function formatRelativeDate(date) {
   const d = toValidDate(date);
   if (!d) return "";

   const divisions = [
      { amount: 60, unit: "seconds" },
      { amount: 60, unit: "minutes" },
      { amount: 24, unit: "hours" },
      { amount: 7, unit: "days" },
      { amount: 4.34524, unit: "weeks" },
      { amount: 12, unit: "months" },
      { amount: Number.POSITIVE_INFINITY, unit: "years" }
   ];

   let duration = (d.getTime() - Date.now()) / 1000;
   const rtf = getRelativeFormatter();
   for (const division of divisions) {
      if (Math.abs(duration) < division.amount) {
         return rtf.format(Math.round(duration), division.unit);
      }
      duration /= division.amount;
   }
   return "";
}
